import { t } from '@lingui/core/macro'
import { checkbox } from '@tecsinapse/cortex-core'
import { Hint, Input, Select } from '@tecsinapse/cortex-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LiaStar, LiaStarSolid } from 'react-icons/lia'
import { ATUALIZAR_GRUPO_FAVORITO } from '../grupo-usuarios-core/data/grupo-usuarios'
import { snackbar } from '@/core/utils/snackbar'
import { isArrayofStrings } from '@/core/utils/is-array-of-strings'
import { useController, useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { FormMultiSelectProps } from './form-multi-select'
import { useMutation } from '@tanstack/react-query'
import {
  AtualizarGrupoFavoritoMutation,
  AtualizarGrupoFavoritoMutationVariables
} from '../../core/types/graphql'
import { request } from '../../core/utils/request'
import { GrupoUsuariosFavoritoOption } from '@/core/utils/types'
import { normalizeString } from '../../core/utils/normalize-string'

interface FormMultiSelectFavoritoProps
  extends Omit<FormMultiSelectProps, 'options' | 'customOnChange'> {
  options: GrupoUsuariosFavoritoOption[]
  customOnChange?: (e: GrupoUsuariosFavoritoOption[]) => void
}

export const FormMultiSelectFavorito = ({
  name,
  label,
  options,
  customOnChange,
  disabled,
  id,
  withSearch = false
}: FormMultiSelectFavoritoProps) => {
  const { control, setValue } = useFormContext()
  const {
    field: { value = [], onBlur },
    fieldState: { error },
    formState: { submitCount, errors }
  } = useController({ name, control })

  const [searchText, setSearchText] = useState('')
  const [localOptions, setLocalOptions] =
    useState<GrupoUsuariosFavoritoOption[]>(options)
  const atualizarGrupoFavorito = useMutation({
    mutationKey: ['atualizarGrupoFavorito'],
    mutationFn: async ({
      variables
    }: {
      variables: AtualizarGrupoFavoritoMutationVariables
    }) =>
      await request<
        AtualizarGrupoFavoritoMutation,
        AtualizarGrupoFavoritoMutationVariables
      >(ATUALIZAR_GRUPO_FAVORITO, { variables })
  })

  useEffect(() => {
    setLocalOptions(options)
  }, [options])

  const sortedOptions = useMemo(() => {
    return [...localOptions].sort((a, b) => {
      if (a.favorite && !b.favorite) return -1
      if (!a.favorite && b.favorite) return 1
      return a.label.localeCompare(b.label)
    })
  }, [localOptions])

  const filteredOptions = useMemo(() => {
    return sortedOptions.filter(option =>
      normalizeString(option.label).includes(normalizeString(searchText))
    )
  }, [sortedOptions, searchText])

  const formatValue = useMemo(() => {
    return isArrayofStrings(value)
      ? localOptions.filter(op => (value as string[]).includes(op.value))
      : localOptions.filter(op =>
          (value as GrupoUsuariosFavoritoOption[]).some(
            item => item.value === op.value
          )
        )
  }, [value, localOptions])

  const selectedValues = useMemo(() => {
    return isArrayofStrings(value)
      ? (value as string[])
      : (value as GrupoUsuariosFavoritoOption[]).map(v => v.value)
  }, [value])

  const allSelected = useMemo(() => {
    return (
      filteredOptions.length > 0 &&
      filteredOptions.every(o => selectedValues.includes(o.value))
    )
  }, [filteredOptions, selectedValues])

  const handleToggleFavorite = useCallback(
    async (grupoId: string) => {
      const updated = localOptions.map(option =>
        option.value === grupoId
          ? { ...option, favorite: !option.favorite }
          : option
      )
      setLocalOptions(updated)
      try {
        await atualizarGrupoFavorito.mutateAsync({ variables: { id: grupoId } })
      } catch {
        setLocalOptions(prev =>
          prev.map(option =>
            option.value === grupoId
              ? { ...option, favorite: !option.favorite }
              : option
          )
        )
        snackbar.show('error', t`Erro ao atualizar favorito. Tente novamente.`)
      }
    },
    [localOptions, atualizarGrupoFavorito]
  )

  const handleSelectAll = useCallback(
    (selectAll: boolean) => {
      const newValues = selectAll ? filteredOptions : []
      setValue(name, newValues, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      })
      if (customOnChange) customOnChange(newValues)
    },
    [filteredOptions]
  )

  return (
    <div className='w-full min-w-0 flex-1'>
      <Select.Root
        value={formatValue}
        keyExtractor={(op: GrupoUsuariosFavoritoOption) => op.value}
        labelExtractor={(op: GrupoUsuariosFavoritoOption) => op.label}
      >
        <Select.Trigger
          label={label ?? ''}
          disabled={disabled}
          multiLabelSelected={num => `${num} itens selecionados`}
          type='button'
          id={id}
          onBlur={onBlur}
        />
        <Select.Popover>
          {withSearch && (
            <Input.Search
              variants={{ className: 'flex-1 mx-deca mt-centi' }}
              onChange={e => setSearchText(e.target.value)}
              placeholder={t`Buscar grupos`}
              defaultValue={searchText}
            />
          )}
          <Select.MultiOptions>
            <Select.CustomOption
              key='select-all'
              option={{
                label: t`Selecionar Todos`,
                value: 'select-all',
                favorite: false
              }}
              onSelectOption={() => handleSelectAll(!allSelected)}
            >
              <div className='flex flex-1 items-center justify-between'>
                <div className='flex items-center'>
                  <input
                    type='checkbox'
                    checked={allSelected}
                    readOnly
                    className={checkbox()}
                  />
                  <p className='ml-deca'>{t`Selecionar Todos`}</p>
                </div>
              </div>
            </Select.CustomOption>
            {filteredOptions.map(option => (
              <Select.CustomMultiOption
                key={option.value}
                option={option}
                onSelect={selected => {
                  setValue(name, selected, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true
                  })
                  if (customOnChange) customOnChange(selected)
                }}
              >
                <div className='flex flex-1 flex-nowrap items-center justify-between'>
                  <p>{option.label}</p>
                  <div
                    className='px-mili'
                    onClick={e => {
                      e.stopPropagation()
                      handleToggleFavorite(option.value)
                    }}
                  >
                    {option.favorite ? (
                      <LiaStarSolid className='text-primary-medium text-deca' />
                    ) : (
                      <LiaStar className='text-deca' />
                    )}
                  </div>
                </div>
              </Select.CustomMultiOption>
            ))}
          </Select.MultiOptions>
        </Select.Popover>
      </Select.Root>
      {submitCount > 0 && error ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => (
            <Hint variants={{ intent: 'error' }}>{message}</Hint>
          )}
        />
      ) : null}
    </div>
  )
}
